<script lang="ts">
import { type IconValues } from '@gat-ship/icons'
import { computed, type HTMLAttributes } from 'vue'
import { cn } from '@gat-ship/vue-components'

export interface RadioGroupItemProps {
  class?: HTMLAttributes['class']
  label: string
  description?: string
  icon?: IconValues
  value: RadioGroupModelValue
  disabled?: boolean
  testId?: string
}
</script>

<script setup lang="ts">
import {
  useRadioGroup,
  type RadioGroupModelValue
} from './radio-group-provider.vue'

const props = defineProps<RadioGroupItemProps>()

const { modelValue, disabled: disabledContext } = useRadioGroup()

function isChecked() {
  return modelValue.value === props.value
}

function getDataState() {
  if (isChecked()) {
    return 'checked'
  }
  return 'unchecked'
}

const isDisabled = computed(() => props.disabled || disabledContext.value)

function handleClick() {
  if (isDisabled.value) {
    return
  }
  modelValue.value = props.value
}
</script>

<template>
  <label :class="cn(
    'bg-background hover:bg-bg-weak-50',
    'p-4',
    'border border-neutral-300 dark:border-neutral-700',
    'ring-inset',
    'data-[state=checked]:z-10 data-[state=checked]:bg-background-lighter data-[state=checked]:ring-1 data-[state=checked]:ring-blue-600',
    'group flex w-full grow select-none flex-col items-start gap-3.5 transition duration-200 ease-out hover:ring-transparent',
    {
      'bg-bg-weak-50 text-text-disabled cursor-not-allowed': isDisabled,
      'cursor-pointer': !isDisabled
    },
    props.class
  )
    " tabindex="0" :for="props.label" :data-disabled="isDisabled" :data-state="getDataState()"
    :aria-disabled="isDisabled" :data-testid="props.testId" @keydown.enter.space="handleClick()"
    @click.stop="handleClick()">
    <div class="flex w-full flex-row items-start items-center gap-3.5">
      <div
        class="bg-background data-[state=checked]:bg-primary shadow-xs ring-border flex size-10 items-center justify-center rounded-full ring-1 ring-inset">
        <slot name="icon" :class="'text-soft group-data-[disabled=true]:text-text-disabled size-5'">
          <gat-icon v-if="icon" :icon="icon" class="text-soft group-data-[disabled=true]:text-text-disabled size-5" />
        </slot>
      </div>
      <div class="flex-1 space-y-1">
        <div class="text-sm font-medium">{{ props.label }}</div>
        <div class="text-soft group-data-[disabled=true]:text-text-disabled text-xs">
          <slot name="description" :disabled="isDisabled">
            <div class="flex flex-col gap-1">
              <span>{{ props.description }}</span>
              <slot name="description-extra" :disabled="isDisabled" />
            </div>
          </slot>
        </div>
      </div>

      <button type="button" role="radio" :aria-checked="isChecked()" :data-state="getDataState()"
        :value="props.value?.toString()" :disabled="isDisabled"
        class="group/radio relative ml-auto size-5 shrink-0 outline-none focus:outline-none"
        :id="typeof props.value === 'string' ? props.value : props.label" tabindex="-1">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <circle cx="10" cy="10" r="8"
            class="fill-bg-soft-200 group-hover/radio:fill-bg-sub-300 group-focus/radio:fill-primary-base group-disabled/radio:fill-bg-soft-200 group-data-[state=checked]/radio:fill-bg-white-0 transition duration-200 ease-out">
          </circle>
          <g filter="url(#:r28:)">
            <circle cx="10" cy="10" r="6.5" class="fill-bg-white-0 group-disabled/radio:hidden"></circle>
          </g>
          <defs>
            <filter id=":r28:" x="1.5" y="3.5" width="17" height="17" filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB">
              <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"></feColorMatrix>
              <feOffset dy="2"></feOffset>
              <feGaussianBlur stdDeviation="1"></feGaussianBlur>
              <feColorMatrix type="matrix" values="0 0 0 0 0.105882 0 0 0 0 0.109804 0 0 0 0 0.113725 0 0 0 0.12 0">
              </feColorMatrix>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_515_4243"></feBlend>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_515_4243" result="shape"></feBlend>
            </filter>
          </defs>
        </svg>
        <svg v-if="isChecked()" width="20" height="20" viewBox="0 0 20 20" fill="none"
          xmlns="http://www.w3.org/2000/svg" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          :data-state="getDataState()">
          <circle cx="10" cy="10" r="6" stroke-width="4"
            class="stroke-primary-base group-hover/radio:stroke-primary-darker group-focus/radio:stroke-primary-dark group-disabled/radio:stroke-bg-soft-200 transition duration-200 ease-out">
          </circle>
        </svg>
      </button>
    </div>
    <slot name="footer" :disabled="isDisabled" />
  </label>
</template>
